import * as React from "react"
import { Check, ChevronsUpDown, Plus, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"

export interface MultiSelectOption {
  value: string
  label: string
  [key: string]: any // Allow additional properties
}

interface MultiSelectComboboxProps {
  options: MultiSelectOption[]
  selectedValues: string[]
  onSelectionChange: (selectedValues: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  allowRemove?: boolean
  renderOption?: (option: MultiSelectOption) => React.ReactNode
  renderSelected?: (option: MultiSelectOption) => React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onEscape?: () => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  singleSelect?: boolean
  createOption?: {
    // Given what the user has typed, so the option can read "Add person
    // \"Ada Byron\"" rather than a generic "Create person" (INCO-69). The
    // difference is whether somebody has to retype the name they just typed.
    label: (search: string) => string
    onSelect: (search: string) => void
  }
}

export function MultiSelectCombobox({
  options,
  selectedValues,
  onSelectionChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search options...",
  emptyMessage = "No options found.",
  className,
  disabled = false,
  allowRemove = true,
  renderOption,
  renderSelected,
  open,
  onOpenChange,
  onEscape,
  onKeyDown,
  singleSelect = false,
  createOption,
}: MultiSelectComboboxProps) {
  const [localOpen, setLocalOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")

  // Filter out selected options from available options
  // In single select mode, include selected options so they can be searched
  const availableOptions = singleSelect 
    ? options 
    : options.filter((option) => !selectedValues.includes(option.value))

  // Get selected options objects
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value)
  )

  const handleSelect = (value: string) => {
    if (singleSelect) {
      // In single select mode, replace the current selection
      onSelectionChange([value])
    } else {
      // In multi select mode, add to existing selection
      const newSelectedValues = [...selectedValues, value]
      onSelectionChange(newSelectedValues)
    }
    setSearchValue("")
    
    // Only close the popover in single select mode
    // For multiselect, keep it open so user can continue selecting
    if (singleSelect) {
      setLocalOpen(false)
      if (onOpenChange) {
        onOpenChange(false)
      }
    }
  }

  const handleRemove = (valueToRemove: string) => {
    const newSelectedValues = selectedValues.filter(
      (value) => value !== valueToRemove
    )
    onSelectionChange(newSelectedValues)
  }

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open || localOpen} onOpenChange={(newOpen) => {
        setLocalOpen(newOpen)
        if (onOpenChange) {
          onOpenChange(newOpen)
        }
      }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open || localOpen}
            className="w-full justify-between min-h-9 h-auto py-1.5"
            disabled={disabled}
          >
            <div className="flex flex-wrap gap-1 max-w-[calc(100%-24px)]">
              {selectedOptions.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : singleSelect ? (
                // In single select mode, show just the label without badge styling
                <span className="text-foreground">
                  {renderSelected ? (
                    renderSelected(selectedOptions[0])
                  ) : (
                    selectedOptions[0].label
                  )}
                </span>
              ) : (
                // In multi select mode, show badges
                selectedOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="mr-1 mb-1 bg-transparent border-primary/20"
                  >
                    {renderSelected ? (
                      renderSelected(option)
                    ) : (
                      <span className="flex items-center gap-1">
                        {option.label}
                      </span>
                    )}
                    {allowRemove && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemove(option.value)
                        }}
                      className="cursor-pointer ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      </button>
                    )}
                  </Badge>
                ))
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-full p-0" 
          align="start"
        >
          <Command
            onKeyDown={(e) => {
              if (onKeyDown) {
                onKeyDown(e);
              }
              if (e.key === "Escape" && onEscape) {
                e.preventDefault();
                onEscape();
              }
            }}
          >
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={setSearchValue}
              onKeyDown={(e) => {
                if (onKeyDown) {
                  onKeyDown(e);
                }
                if (e.key === "Escape" && onEscape) {
                  e.preventDefault();
                  onEscape();
                }
              }}
            />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {availableOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleSelect(option.value)}
                  >
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <span>{option.label}</span>
                    )}
                  </CommandItem>
                ))}
                {createOption && (
                  <CommandItem
                    key="__create__"
                    // Constant, so cmdk never filters the create row out of
                    // its own results — the whole point is that it survives a
                    // search that matched nothing.
                    value="__create__"
                    onSelect={() => {
                      createOption.onSelect(searchValue)
                      setLocalOpen(false)
                      if (onOpenChange) {
                        onOpenChange(false)
                      }
                    }}
                    className="text-muted-foreground"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>{createOption.label(searchValue)}</span>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
} 